import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { useApiClient } from '@@common';

import { ParameterMetadata, ParameterValueOption, ServiceConnectionClient } from '../../../api/api';
import { getNestedValue } from '../Models/PropertyHelpers';

export const SERVICE_CONNECTION_PROPERTY_OPTIONS_SOURCE_KIND = 'ServiceConnectionProperty';

export function getValueOptionKey(option: ParameterValueOption) {
	return `${option.name}:${JSON.stringify(option.value)}`;
}

export function compareValueOptions(left: ParameterValueOption, right: ParameterValueOption) {
	return left.name.localeCompare(right.name, undefined, { sensitivity: 'base' });
}

export function getValueOptionLabel(valueOptions: ParameterValueOption[], value: unknown) {
	return valueOptions.find((valueOption) => String(valueOption.value) === String(value))?.name ?? String(value);
}

export function compareSelectedValues(valueOptions: ParameterValueOption[], left: string, right: string) {
	return getValueOptionLabel(valueOptions, left).localeCompare(getValueOptionLabel(valueOptions, right), undefined, { sensitivity: 'base' });
}

export function sortSelectedValues(valueOptions: ParameterValueOption[], values: string[]) {
	return [...values].sort((left, right) => compareSelectedValues(valueOptions, left, right));
}

export function mergeValueOptions(...sources: (ParameterValueOption[] | undefined)[]) {
	const options = new Map<string, ParameterValueOption>();

	for (const source of sources) {
		for (const option of source ?? []) {
			options.set(getValueOptionKey(option), option);
		}
	}

	return Array.from(options.values()).sort(compareValueOptions);
}

export function hasValueOption(valueOptions: ParameterValueOption[], value: unknown): boolean {
	return valueOptions.some((option) => String(option.value) === String(value));
}

export function isWildcardValue(value: unknown): boolean {
	return typeof value === 'string' && value.includes('*');
}

function getPropertyValueIgnoreCase(obj: any, name: string): any {
	if (!obj || typeof obj !== 'object') {
		return undefined;
	}

	const matchingKey = Object.keys(obj).find((key) => key.localeCompare(name, undefined, { sensitivity: 'accent' }) === 0);
	return matchingKey ? obj[matchingKey] : undefined;
}

function getNestedValueIgnoreCase(obj: any, path: string): any {
	if (!path.includes('.')) {
		return getPropertyValueIgnoreCase(obj, path);
	}

	let current = obj;
	for (const key of path.split('.')) {
		if (!current || typeof current !== 'object') {
			return undefined;
		}

		current = getPropertyValueIgnoreCase(current, key);
	}

	return current;
}

export function getModelValueByNameRef(model: Record<string, any> | undefined, nameRef: string): any {
	if (!model) {
		return undefined;
	}

	const pathParts = nameRef.split('.');
	const lastPathPart = pathParts[pathParts.length - 1] ?? nameRef;

	return getNestedValue(model, nameRef)
		?? getNestedValueIgnoreCase(model, nameRef)
		?? getPropertyValueIgnoreCase(model, lastPathPart);
}

export function getServiceConnectionId(serviceConnectionRef: any): string | undefined {
	if (!serviceConnectionRef || typeof serviceConnectionRef !== 'object') {
		return undefined;
	}

	return serviceConnectionRef.Id ?? serviceConnectionRef.id;
}

function hasMeaningfulValue(value: any): boolean {
	return value !== null && value !== undefined && value !== '';
}

export function useParameterValueOptions(parameterMetadata: ParameterMetadata, model?: Record<string, any>, enabled: boolean = true) {
	const serviceConnectionClient = useApiClient(ServiceConnectionClient);
	const valueOptionsSource = parameterMetadata.valueOptionsSource;
	const serviceConnectionRef =
		valueOptionsSource?.serviceConnectionNameRef && model
			? getModelValueByNameRef(model, valueOptionsSource.serviceConnectionNameRef)
			: undefined;
	const serviceConnectionId = getServiceConnectionId(serviceConnectionRef);
	const hasServiceConnectionPropertyOptionsSource =
		valueOptionsSource?.kind === SERVICE_CONNECTION_PROPERTY_OPTIONS_SOURCE_KIND && !!valueOptionsSource.propertyName;
	const dependentValueNameRefs = useMemo(() => valueOptionsSource?.dependentValueNameRefs ?? [], [valueOptionsSource?.dependentValueNameRefs]);
	const dependentValues = useMemo(() => {
		if (!model || dependentValueNameRefs.length === 0) {
			return undefined;
		}

		return Object.fromEntries(
			dependentValueNameRefs.map((nameRef) => [
				nameRef,
				getModelValueByNameRef(model, nameRef),
			]),
		);
	}, [dependentValueNameRefs, model]);
	const missingDependentValueNameRef = dependentValueNameRefs.find((nameRef) => !hasMeaningfulValue(dependentValues?.[nameRef]));
	const canLoadServiceConnectionPropertyOptions =
		hasServiceConnectionPropertyOptionsSource
		&& !!serviceConnectionId
		&& !missingDependentValueNameRef;

	const { data: serviceConnectionProperties } = useQuery({
		queryKey: [
			'service-connection',
			'properties',
			serviceConnectionId,
			valueOptionsSource?.propertyName,
			JSON.stringify(dependentValues ?? {}),
		],
		queryFn: async ({ signal }) => {
			if (dependentValueNameRefs.length > 0) {
				const property = await serviceConnectionClient.getProperty(
					serviceConnectionId!,
					valueOptionsSource!.propertyName,
					{ dependentValues },
					signal);

				return property ? [property] : [];
			}

			return serviceConnectionClient.getProperties(serviceConnectionId!, undefined, signal);
		},
		enabled: enabled && canLoadServiceConnectionPropertyOptions,
	});

	const dynamicValueOptions = useMemo(() => {
		if (!canLoadServiceConnectionPropertyOptions || !serviceConnectionProperties) {
			return undefined;
		}

		return serviceConnectionProperties.find((property) => property.name === valueOptionsSource?.propertyName)?.valueOptions;
	}, [canLoadServiceConnectionPropertyOptions, serviceConnectionProperties, valueOptionsSource?.propertyName]);

	const valueOptions = useMemo(() => mergeValueOptions(parameterMetadata.valueOptions, dynamicValueOptions), [parameterMetadata.valueOptions, dynamicValueOptions]);
	const hasLoadedValueOptionsSource = hasServiceConnectionPropertyOptionsSource && !!serviceConnectionProperties;

	return {
		valueOptions,
		valueOptionsSource,
		hasServiceConnectionPropertyOptionsSource,
		hasLoadedValueOptionsSource,
		serviceConnectionId,
		missingDependentValueNameRef,
	};
}
